import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Skeleton } from '@components/ui/skeleton';
import { useGetMeiaStudy, useGetMeiaStudyMissionThreads, useGetMeiaStudyMissions } from '@queries/meia.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import { canAccessMeiaStudioV1 } from '@utils/meia-access.util';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { MeiaAcrMatrix } from './components/meia-acr-matrix.component';
import { MeiaAcrRegisterModal } from './components/meia-acr-register-modal.component';
import { MeiaAcrShield } from './components/meia-acr-shield.component';
import { getMeiaStudyThreadsLabel } from './components/meia-mission-display.util';
import { MeiaP1Authoring } from './components/meia-p1-authoring.component';
import { MeiaP2Authoring } from './components/meia-p2-authoring.component';
import { MeiaP3Authoring } from './components/meia-p3-authoring.component';
import { MeiaP4Authoring } from './components/meia-p4-authoring.component';
import { MeiaP5Brief } from './components/meia-p5-brief.component';
import { MeiaStudyEditModal } from './components/meia-study-create-modal.component';
import { MeiaStudyHeader } from './components/meia-study-header.component';
import { MeiaStudyOverview } from './components/meia-study-overview.component';
import { type MeiaTabKey, isMeiaTabKey, meiaRoute } from './meia.constants';

const DEFAULT_TAB: MeiaTabKey = 'overview';

function MeiaShellLoadingState(): JSX.Element {
  return (
    <MainLayout
      className="max-w-full"
      fullWidth
    >
      <div className="flex flex-col gap-4">
        <Skeleton className="h-32 w-full" />
        <div className="mx-auto w-full max-w-[1600px] px-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Skeleton className="h-[520px] w-full" />
            <Skeleton className="h-[520px] w-full" />
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export const MeiaStudyShellPage: FC = () => {
  const { studyId, tab: rawTab } = useParams<{ studyId: string; tab?: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAcrModalOpen, setIsAcrModalOpen] = useState(false);
  const [focusedAcrId, setFocusedAcrId] = useState<string | null>(null);
  const activeTab: MeiaTabKey = rawTab && isMeiaTabKey(rawTab) ? rawTab : DEFAULT_TAB;

  const {
    data: study,
    error: studyError,
    isError: isStudyError,
    isLoading: isStudyLoading,
  } = useGetMeiaStudy({ studyId });
  const { data: missions = [], isLoading: isMissionsLoading } = useGetMeiaStudyMissions({ studyId });
  const { data: currentUser, isPending: isUserPending } = useGetCurrentUser();
  const { threads } = useGetMeiaStudyMissionThreads({ missions, studyId });
  const threadsLabel = useMemo(() => getMeiaStudyThreadsLabel(threads), [threads]);

  if (!isStudyLoading && !isUserPending && !canAccessMeiaStudioV1(currentUser)) {
    return <Navigate to={routes.home.path} />;
  }

  if (isStudyLoading || isUserPending) return <MeiaShellLoadingState />;

  if (!studyId || isStudyError || !study) {
    return (
      <MainLayout
        className="max-w-full"
        fullWidth
      >
        <div className="mx-auto w-full max-w-5xl px-4 pt-6">
          <div className="rounded-md border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            {studyError?.errors[0]?.message ?? 'Unable to load MEIA study.'}
          </div>
        </div>
      </MainLayout>
    );
  }

  const handleTabChange = (tab: MeiaTabKey): void => {
    navigate(meiaRoute.studyTab({ studyId, tab }));
  };

  const canEdit = study.canEditStudy;

  const openAcrRegister = (focusEntryId?: string | null): void => {
    setFocusedAcrId(focusEntryId ?? null);
    setIsAcrModalOpen(true);
  };

  return (
    <PageviewTracker>
      <HelmetWrapper title={`${study.name} | MEIA Studio`} />
      <MainLayout
        className="max-w-full"
        fullWidth
      >
        <div className="flex flex-col">
          <MeiaStudyHeader
            acrSlot={
              <MeiaAcrShield
                onOpen={() => openAcrRegister()}
                studyId={studyId}
              />
            }
            activeTab={activeTab}
            canEdit={canEdit}
            missionsCount={missions.length}
            onEdit={() => setIsEditModalOpen(true)}
            onTabChange={handleTabChange}
            study={study}
            threadsLabel={threads.length > 0 ? threadsLabel : undefined}
          />

          <div
            className="@container/meia-study mx-auto w-full max-w-[1600px] px-4 pt-6"
            data-meia-study-container="true"
          >
            {activeTab === 'overview' && (
              <MeiaStudyOverview
                currentUserId={currentUser?.id}
                isMissionsLoading={isMissionsLoading}
                missions={missions}
                study={study}
              />
            )}

            {activeTab === 'p1' && (
              <MeiaP1Authoring
                canEdit={canEdit}
                currentUserId={currentUser?.id}
                missions={missions}
                study={study}
              />
            )}

            {activeTab === 'p2' && (
              <MeiaP2Authoring
                canEdit={canEdit}
                isMissionsLoading={isMissionsLoading}
                missions={missions}
                study={study}
                studyId={studyId}
              />
            )}

            {activeTab === 'p3' && (
              <MeiaP3Authoring
                canEdit={canEdit}
                isMissionsLoading={isMissionsLoading}
                missions={missions}
                study={study}
                studyId={studyId}
              />
            )}

            {activeTab === 'p4' && (
              <MeiaP4Authoring
                canEdit={canEdit}
                isMissionsLoading={isMissionsLoading}
                missions={missions}
                study={study}
                studyId={studyId}
              />
            )}

            {activeTab === 'p5' && (
              <MeiaP5Brief
                isMissionsLoading={isMissionsLoading}
                missions={missions}
                onOpenAcrRegister={openAcrRegister}
                study={study}
                studyId={studyId}
              />
            )}

            {activeTab === 'acr-matrix' && (
              <MeiaAcrMatrix
                onSelectEntry={openAcrRegister}
                studyId={studyId}
              />
            )}

            {isMissionsLoading && missions.length === 0 && activeTab !== 'overview' && activeTab !== 'acr-matrix' && (
              <Skeleton className="h-[400px] w-full" />
            )}
          </div>
        </div>
      </MainLayout>
      {canEdit && (
        <MeiaStudyEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          study={study}
        />
      )}
      <MeiaAcrRegisterModal
        canEdit={canEdit}
        focusEntryId={focusedAcrId}
        isOpen={isAcrModalOpen}
        onClose={() => {
          setIsAcrModalOpen(false);
          setFocusedAcrId(null);
        }}
        studyId={studyId}
      />
    </PageviewTracker>
  );
};
